/* eslint-disable react-hooks/exhaustive-deps */
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { debounce } from "lodash";
import React from "react";
import { api } from "../../api/api";
import { CustomFormLabel, CustomTextField } from "../formsComponents";
import { IPartner } from "../../views/partner/interface/IPartner";

export interface IPartnerAutosearch {
  uniqueKey: string;
  label?: string;
  column?: { label: string; value: string };
  value?: { partner_id?: number | null; partner_name: string } | null;
  onSelect: (data: IPartner) => void;
  disabled?: boolean;
  error?: string;
  hideAddButton?: boolean;
  filter?: boolean;
}

export const defaultPartner:IPartner = {
  partner_id: null,
  partner_name: "",
  partner_type: "",
  partner_terms: [],
  status: "INACTIVE" as const,
  comment: "",
  insert_dt: "",
};

export const PartnerAutoSearch: React.FC<IPartnerAutosearch> = (props) => {
  const {
    uniqueKey,
    label,
    value,
    onSelect,
    disabled,
    error,
    filter,
  } = props;

  const [options, setOptions] = React.useState<IPartner[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearchText] = React.useState<any>("");
  const [openUserDialog, setOpenUserDialog] = React.useState(false);
  const [hasFetchedInitialData, setHasFetchedInitialData] = React.useState(false);
  const fetchSuggestion = async (enteredValue: string) => {
    setLoading(true);
    try {
      let searchQuery = "";
      
      if (enteredValue.length > 0) {
        searchQuery = `partner_name=${enteredValue}&`;
      }

      const res = await api.get(
        `/get-partners?${searchQuery}pageNo=1&itemsPerPage=20`
      );

      const data: IPartner[] = res.data.result;
      
      // Filter data to only include partners with ID > 2
      const filteredData = filter ? data.filter(partner => partner?.partner_id && partner.partner_id > 2) : data;
     
      setOptions(filteredData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const debounceFn = React.useCallback(debounce(fetchSuggestion, 800), []);

  const getOptionLabel = (option: IPartner | string) => {
    if (typeof option === "string") return option || "";
    return value?.partner_id === option.partner_id
      ? option.partner_name
      : option.partner_name ;
  };

  const getValue = () => {
    return options.find((opt) => opt.partner_id === value?.partner_id) || null;
  };

  const handleToggle = () => {
    setOpenUserDialog(!openUserDialog);
  };

  const handleComplete = (partner: IPartner) => {
    setOptions([partner, ...options]);
    onSelect(partner);
    handleToggle();
  };

  const handleDropDownOnFocus = () => {
    if (search.length === 0 && options.length === 0) {
      fetchSuggestion("");
      
    }
  };
  React.useEffect(() => {
    fetchSuggestion("");
  }, []);

  React.useEffect(() => {
    if (search && search.length > 0) {
      debounceFn(search);
    }
  }, [search]);

//   React.useEffect(() => {
//     if (value && typeof value === "object" && value?.partner_id) {
//       const option: IPartner = {
//         ...defaultPartner,
//         partner_id: value.partner_id,
//         partner_name: value.partner_name,
//       };
//       setOptions([option]);
//     }
//   }, [value]);

  return (
    <>
      {label && (
        <Box display="flex" justifyContent={"flex-start"} alignItems="center">
          <CustomFormLabel display={"block"}>{` ${label}  `}</CustomFormLabel>
          {search.length > 0 && options.length === 0 && !loading && (
            <Box
              sx={{
                paddingLeft: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                marginTop: 1,
              }}
              onClick={handleToggle}
            >
              <AddCircleIcon fontSize="small" color="primary" />
            </Box>
          )}
        </Box>
      )}
      <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
        <Autocomplete
          id={uniqueKey}
          freeSolo
          fullWidth
          disabled={disabled}
          sx={{
            ".MuiOutlinedInput-root": {
              paddingTop: "2px",
              paddingBottom: "2px",
              fontSize: "0.8rem",
              color: "rgb(38, 38, 38)",
              width: "100%",
              backgroundColor: disabled ? "#f8f9fb" : "inherit",
            },
          }}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, value) =>
            typeof option === "string"
              ? option === value
              : typeof value === "object" && option.partner_name === value.partner_name
          }
          filterOptions={(x) => x}
          options={options}
          autoComplete
          includeInputInList
          value={getValue()}
          loading={loading}
          noOptionsText="No Partner Found"
          onFocus={handleDropDownOnFocus}
          onChange={(event: React.SyntheticEvent, newValue: string | IPartner | null) => {
            if (!newValue) {
              onSelect(defaultPartner);
            } else if (typeof newValue === "string") {
              const newPlan: IPartner = {
                ...defaultPartner,
                partner_name: newValue,
              };
              if (!options.some(opt => opt.partner_name === newValue)) {
                setOptions([newPlan, ...options]);
              }
              onSelect(newPlan);
            } else {
              if (!options.some(opt => opt.partner_id === newValue.partner_id)) {
                setOptions([newValue, ...options]);
              }
              onSelect(newValue);
            }
          }}
          onInputChange={(event, newInputValue) => {
            // When the input value is cleared (cross button), reset the state
            if (newInputValue === "" || newInputValue === null) {
              setSearchText("");
              onSelect(defaultPartner); // <-- Reset the selected plan
            } else if (event && event.type === "change") {
              setSearchText(newInputValue);
            }
          }}
          renderInput={(params: any) => (
            <CustomTextField
            {...params}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            disabled={disabled}
            error={!!error}
            helperText={error}
            placeholder="Search Partner"
          />
          )}
          
        />
      </Stack>
    </>
  );
}; 